import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tree } from "primereact/tree";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Langs from "../../../../app/lang/langs";
import {
    getProjectProgress,
    updateNeededCollabsPerSession,
    setNeededCollabsPerSession,
} from "../../vcompSlice";
import MobileAptitudeModal from "./MobileAptitudeModal";
import MobileNumberStepper from "./MobileNumberStepper";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const getColor = (num) => {
    if (num < 0 || num > 100) return "#9e9e9e";
    if (num < 25) return "#f44336";
    if (num < 50) return "#FF9015";
    if (num < 75) return "#FFBF00";
    return "#4caf50";
};

const getMuiProgressColor = (num) => {
    if (num < 25) return "#f44336";
    if (num < 50) return "#FF9015";
    if (num < 75) return "#FFBF00";
    return "#4caf50";
};

export default function MobileProgressView() {
    const dispatch = useDispatch();

    const progressDetails = useSelector((state) => state.vcomp.projectProgressDetails);
    const projectProgress = useSelector((state) => state.vcomp.projectProgress);
    const onEditProject = useSelector((state) => state.vcomp.onCreateProject);
    const mode = useSelector((state) => state.vcomp.projectMode);
    const collabsNames = useSelector((state) => state.vcomp.collabsNames);
    const collabsIds = useSelector((state) => state.vcomp.projectCollabs);
    const sessionStartDate = useSelector((state) => state.vcomp.sessionStartDate);
    const sessionEndDate = useSelector((state) => state.vcomp.sessionEndDate);
    const neededCollabsNumber = useSelector((state) => state.vcomp.neededCollabsPerSession);

    const [nodes, setNodes] = useState([]);
    const [sessionNumber, setSesssionNumber] = useState(0);
    const [maxSession, setMaxSession] = useState(0);
    const [collabsList, setCollabsList] = useState([]);
    const [associatedMembers, setAssociatedMembers] = useState(null);
    const [localProg, setLocalProg] = useState(0);
    const [neededCollabs, setNeededCollabs] = useState(0);

    const [showModal, setShowModal] = useState(false);
    const [selectedAptitude, setSelectedAptitude] = useState(null);
    const [editAptitudeDetails, setEditAptitudeDetails] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState({ "0": true, "0-0": true });

    const handleClose = () => {
        setShowModal(false);
        setSelectedAptitude(null);
        setEditAptitudeDetails(false);
    };

    const handleShow = () => setShowModal(true);

    const expandAll = () => {
        let _expandedKeys = {};
        for (let node of nodes) {
            expandNode(node, _expandedKeys);
        }
        setExpandedKeys(_expandedKeys);
    };

    const collapseAll = () => {
        setExpandedKeys({});
    };

    const expandNode = (node, _expandedKeys) => {
        if (node.children && node.children.length) {
            _expandedKeys[node.key] = true;
            for (let child of node.children) {
                expandNode(child, _expandedKeys);
            }
        }
    };

    // Process collabs into dropdown list
    // Depends on BOTH lists — see the same effect in ProgressView.js. getCollabsNames()
    // and getProjectMembersAndManagers() race, the ids normally win, and keyed only on
    // collabsIds this ran once against an empty directory and left the dropdown blank.
    useEffect(() => {
        const idCollabs = (collabsIds || []).map((collab) => collab.id_collab);
        setCollabsList(
            (collabsNames || [])
                .filter((collab) => idCollabs.includes(collab.id))
                .map(({ id, nom, prenom }) => ({
                    id,
                    name: `${nom} ${prenom}`,
                }))
        );
    }, [collabsIds, collabsNames]);

    // Recalculate local progress
    useEffect(() => {
        if (associatedMembers && associatedMembers.id) {
            const categories = filterByCollabIdAndRecalculateScores(nodes, associatedMembers);
            const totalCoef = categories.reduce((acc, cat) => acc + (cat.coef || 1), 0);
            const weightedSum = categories.reduce(
                (acc, cat) => acc + (cat.progress || 0) * (cat.coef || 1),
                0
            );
            const averageProgress = totalCoef > 0 ? weightedSum / totalCoef : 0;
            setLocalProg(parseFloat(averageProgress).toFixed(2));
        } else {
            setLocalProg(projectProgress);
        }
    }, [associatedMembers, nodes, progressDetails]);

    // Sync needed collabs from redux
    useEffect(() => {
        setNeededCollabs(neededCollabsNumber);
    }, [neededCollabsNumber]);

    // Initialize session/progress when project changes
    useEffect(() => {
        if (onEditProject && onEditProject.id) {
            if (mode === "edit") {
                setSesssionNumber(onEditProject.last_session_number + 1);
                setMaxSession(onEditProject.last_session_number + 1);
                setLocalProg(projectProgress);
            } else {
                setSesssionNumber(0);
                setMaxSession(0);
                setLocalProg(0);
                setCollabsList([]);
                setAssociatedMembers(null);
                setNodes([]);
            }
        } else {
            setSesssionNumber(0);
            setMaxSession(0);
            setLocalProg(0);
            setCollabsList([]);
            setAssociatedMembers(null);
            setNodes([]);
        }
    }, [onEditProject]);

    // Process progressDetails into tree nodes
    useEffect(() => {
        if (progressDetails.length > 0) {
            if (progressDetails?.length > 0) {
                const excludedKeys = [];
                const processNode = (node) => {
                    const keyPrefix =
                        node.scope === "category"
                            ? "cat"
                            : node.scope === "subcategory"
                            ? "subcat"
                            : "apt";
                    const key = `${keyPrefix}-${node.id}`;
                    let processedChildren = node.children
                        ? node.children.map(processNode).filter((child) => child !== undefined)
                        : [];
                    if (
                        (keyPrefix === "cat" || keyPrefix === "subcat") &&
                        processedChildren.length === 0
                    ) {
                        return undefined;
                    }
                    if (!excludedKeys.includes(key)) {
                        return {
                            key: key,
                            id: node.id,
                            label: node.nom,
                            progress: node.progress,
                            coef: node.coef,
                            className: node.scope,
                            pos: node.pos,
                            visibility: node.visibility,
                            description: node.description,
                            type: node.type,
                            aspect: node.aspect ? node.aspect : null,
                            collabs_answers: node.collabs_answers ? node.collabs_answers : [],
                            collabs_no_answers: node.collabs_no_answers
                                ? node.collabs_no_answers
                                : [],
                            children: node.children
                                ? node.children
                                      .map((child) => processNode(child))
                                      .filter((child) => child !== undefined)
                                : [],
                            questions: node.questions ? node.questions : [],
                        };
                    }
                };

                const sdata = progressDetails.map((category) => processNode(category));
                setNodes(sdata.filter((s) => s !== undefined));
            }
        } else {
            setNodes([]);
        }
    }, [progressDetails]);

    function findNodeByKey(tree, key) {
        if (!tree) return null;
        if (!key) return null;
        for (let node of tree) {
            if (node.key === key) return node;
            if (node.children && node.children.length) {
                const found = findNodeByKey(node.children, key);
                if (found) return found;
            }
        }
        return null;
    }

    function filterByCollabIdAndRecalculateScores(data, collabId) {
        const dataClone = JSON.parse(JSON.stringify(data));
        function recurse(elements) {
            return elements.map((element) => {
                if (element.className === "aptitude") {
                    const collabAnswers = element.collabs_answers?.find(
                        (ca) => ca.id_collab === collabId.id
                    );
                    const collabNoAnswers = element.collabs_no_answers?.some(
                        (ca) => ca.id === collabId.id
                    );
                    if (collabAnswers) {
                        element.progress = collabAnswers.progress;
                    } else if (collabNoAnswers) {
                        element.progress = 0;
                    }
                }
                if (element.children && element.children.length > 0) {
                    element.children = recurse(element.children);
                    const totalCoef = element.children.reduce(
                        (acc, child) => acc + child.coef,
                        0
                    );
                    const weightedSum = element.children.reduce(
                        (acc, child) => acc + child.progress * child.coef,
                        0
                    );
                    const averageProgress = totalCoef > 0 ? weightedSum / totalCoef : 0;
                    element.progress = averageProgress;
                }
                return element;
            });
        }
        if (associatedMembers && associatedMembers.id) {
            return recurse(dataClone);
        } else {
            return nodes;
        }
    }

    const flattenTree = (treeNodes, rows = [], parentCategory = "", parentSubcategory = "") => {
        for (const node of treeNodes) {
            if (node.className === "category") {
                flattenTree(node.children || [], rows, node.label, "");
            } else if (node.className === "subcategory") {
                flattenTree(node.children || [], rows, parentCategory, node.label);
            } else if (node.className === "aptitude") {
                rows.push({
                    category: parentCategory,
                    subcategory: parentSubcategory,
                    aptitude: node.label,
                    aspect:
                        node.aspect === "theoretical"
                            ? "Théorique"
                            : node.aspect === "practical"
                            ? "Pratique"
                            : "",
                    progress: parseFloat(Number(node.progress || 0).toFixed(2)),
                    coef: node.coef || 1,
                });
            }
        }
        return rows;
    };

    const exportProgress = async () => {
        const treeData = filterByCollabIdAndRecalculateScores(nodes, associatedMembers);
        const rows = flattenTree(treeData);

        const workbook = new ExcelJS.Workbook();
        workbook.creator = "Groupe Vital Dev";
        workbook.created = new Date();

        const projectName = onEditProject?.nom || "Projet";
        const collabName = associatedMembers?.name || null;
        const sheetTitle = collabName
            ? `Progression - ${collabName}`
            : "Progression équipe";
        const sheet = workbook.addWorksheet(sheetTitle.substring(0, 31));

        const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF9015" } };
        const borderStyle = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
        };
        const headerFont = { bold: true, color: { argb: "FFFFFF" }, size: 11 };

        // Title row
        const titleRow = sheet.addRow([
            `${projectName} - ${collabName || "Équipe complète"} - Session ${sessionNumber}`,
        ]);
        titleRow.font = { bold: true, size: 14 };
        sheet.mergeCells(1, 1, 1, 6);
        titleRow.getCell(1).alignment = { horizontal: "center" };

        // Progress row
        const progRow = sheet.addRow([`Progression globale : ${localProg}%`]);
        progRow.font = { bold: true, size: 11 };
        sheet.mergeCells(2, 1, 2, 6);
        progRow.getCell(1).alignment = { horizontal: "center" };

        sheet.addRow([]);

        // Header row
        const headers = [
            "Catégorie",
            "Sous-catégorie",
            "Aptitude",
            "Aspect",
            "Progression (%)",
            "Coefficient",
        ];
        const headerRow = sheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.fill = headerFill;
            cell.font = headerFont;
            cell.border = borderStyle;
            cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        });

        // Data rows
        rows.forEach((r) => {
            const row = sheet.addRow([
                r.category,
                r.subcategory,
                r.aptitude,
                r.aspect,
                r.progress,
                r.coef,
            ]);
            row.eachCell((cell, colNumber) => {
                cell.border = borderStyle;
                cell.alignment = {
                    vertical: "middle",
                    horizontal: colNumber >= 5 ? "center" : "left",
                    wrapText: true,
                };
            });
            const progressCell = row.getCell(5);
            let color = "FF808080";
            if (r.progress >= 75) color = "FF00B050";
            else if (r.progress >= 50) color = "FFFFBF00";
            else if (r.progress >= 25) color = "FFFF9015";
            else color = "FFFF0000";
            progressCell.font = { bold: true, color: { argb: color } };
        });

        // Category summary rows
        const categories = treeData.filter((n) => n.className === "category");
        sheet.addRow([]);
        const summaryHeaderRow = sheet.addRow(["Résumé par catégorie", "", "", "", "", ""]);
        summaryHeaderRow.font = { bold: true, size: 12 };
        sheet.mergeCells(summaryHeaderRow.number, 1, summaryHeaderRow.number, 6);

        const catHeaderRow = sheet.addRow([
            "Catégorie",
            "",
            "",
            "",
            "Progression (%)",
            "Coefficient",
        ]);
        catHeaderRow.eachCell((cell) => {
            cell.fill = headerFill;
            cell.font = headerFont;
            cell.border = borderStyle;
            cell.alignment = { vertical: "middle", horizontal: "center" };
        });
        sheet.mergeCells(catHeaderRow.number, 1, catHeaderRow.number, 4);

        categories.forEach((cat) => {
            const row = sheet.addRow([
                cat.label,
                "",
                "",
                "",
                parseFloat(Number(cat.progress || 0).toFixed(2)),
                cat.coef || 1,
            ]);
            sheet.mergeCells(row.number, 1, row.number, 4);
            row.eachCell((cell) => {
                cell.border = borderStyle;
                cell.alignment = { vertical: "middle", horizontal: "center" };
            });
        });

        // Auto-width columns
        sheet.columns.forEach((column) => {
            let maxLength = 10;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const len = cell.value ? cell.value.toString().length : 0;
                maxLength = Math.max(maxLength, Math.min(len, 50));
            });
            column.width = maxLength + 2;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer]);
        const fileName = collabName
            ? `Progression_${collabName.replace(/\s+/g, "_")}_Session${sessionNumber}.xlsx`
            : `Progression_Équipe_Session${sessionNumber}.xlsx`;
        saveAs(blob, fileName);
    };

    const handleSessionNumberChange = (e) => {
        if (e.value == sessionNumber) return;
        setSesssionNumber(e.value);
        if (onEditProject && mode === "edit" && sessionNumber >= 1) {
            let sn = e.value - 1;
            dispatch(getProjectProgress({ id_project: onEditProject.id, session: sn }));
        }
    };

    const handleNeededCollabsChange = (e) => {
        if (e.value == neededCollabs) return;
        setNeededCollabs(e.value);
        if (onEditProject && mode === "edit" && sessionNumber >= 1) {
            let sn = sessionNumber - 1;
            dispatch(
                updateNeededCollabsPerSession({
                    id_project: onEditProject.id,
                    session_number: sn,
                    needed_collabs: e.value,
                })
            ).then(() => {
                dispatch(setNeededCollabsPerSession(e.value));
            });
        }
    };

    const viewCollabsRespNodeDoubleClick = (value) => {
        if (associatedMembers) return;
        setEditAptitudeDetails(true);
        if (onEditProject && sessionNumber) {
            if (sessionNumber - 1 != onEditProject.last_session_number) {
                setEditAptitudeDetails(false);
            }
        }
        if (value.node.className === "aptitude") {
            setSelectedAptitude(value.node.key);
            handleShow();
        }
    };

    const nodeTemplate = (node) => {
        const progressVal = parseFloat(Number(node.progress || 0).toFixed(1));
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: 1,
                    py: 0.5,
                    minHeight: 44,
                }}
            >
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: "0.85rem",
                            fontWeight: node.className === "category" ? 600 : 400,
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                        }}
                    >
                        {node.label}
                        {node.aspect === "theoretical" && (
                            <Typography
                                component="span"
                                sx={{ fontWeight: 700, ml: 0.5, fontSize: "0.8rem" }}
                            >
                                [T]
                            </Typography>
                        )}
                        {node.aspect === "practical" && (
                            <Typography
                                component="span"
                                sx={{ fontWeight: 700, ml: 0.5, fontSize: "0.8rem" }}
                            >
                                [P]
                            </Typography>
                        )}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: "0 0 110px",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                    }}
                >
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(progressVal, 100)}
                        sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "rgba(0,0,0,0.06)",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 4,
                                bgcolor: getMuiProgressColor(progressVal),
                            },
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: getMuiProgressColor(progressVal),
                            minWidth: 32,
                            textAlign: "right",
                        }}
                    >
                        {progressVal}%
                    </Typography>
                </Box>
            </Box>
        );
    };

    const treeData = filterByCollabIdAndRecalculateScores(nodes, associatedMembers);

    return (
        <Box sx={{ mt: 0, pb: 2 }}>
            {/* Collaborator filter */}
            <Box sx={{ px: 0, mb: 2 }}>
                <Autocomplete
                    value={associatedMembers}
                    onChange={(_, newValue) => setAssociatedMembers(newValue)}
                    options={collabsList}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    slotProps={{
                        popper: { sx: { zIndex: 1500 } },
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Sélectionner un collaborateur"
                            size="small"
                            sx={{
                                "& input": { fontSize: "16px" },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 3,
                                    minHeight: 48,
                                },
                            }}
                        />
                    )}
                    sx={{ mb: 2 }}
                />

                {/* Session stepper + Needed collabs on one row */}
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1 }}>
                        <MobileNumberStepper
                            label={<Langs str="Choose Session" />}
                            value={sessionNumber}
                            onChange={handleSessionNumberChange}
                            min={1}
                            max={maxSession}
                            step={1}
                        />
                    </Box>
                    {mode === "edit" && (
                        <Box sx={{ flex: 1 }}>
                            <MobileNumberStepper
                                label={<Langs str="NEEDED_COLLABS_NUMBER" />}
                                value={neededCollabs}
                                onChange={handleNeededCollabsChange}
                                min={0}
                                step={1}
                            />
                        </Box>
                    )}
                </Box>

                {/* Session dates */}
                {(sessionStartDate || sessionEndDate) && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1.5,
                            px: 1,
                            py: 1,
                            bgcolor: "rgba(0,0,0,0.02)",
                            borderRadius: 2,
                        }}
                    >
                        <CalendarTodayIcon sx={{ fontSize: 18, color: "#999" }} />
                        <Typography variant="caption" sx={{ color: "#7c7b7b", fontSize: "0.8rem" }}>
                            {sessionStartDate && (
                                <>
                                    <Langs str="START_DATE" />:{" "}
                                    {moment(sessionStartDate).format("DD-MM-YYYY")}
                                </>
                            )}
                            {sessionStartDate && sessionEndDate && " — "}
                            {sessionEndDate && (
                                <>
                                    <Langs str="END_DATE" />:{" "}
                                    {moment(sessionEndDate).format("DD-MM-YYYY")}
                                </>
                            )}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Overall progress */}
            <Box sx={{ px: 0, mb: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
                        <Langs str="Overall Progress" />
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            color: getMuiProgressColor(parseFloat(localProg)),
                        }}
                    >
                        {localProg}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(parseFloat(localProg) || 0, 100)}
                    sx={{
                        height: 12,
                        borderRadius: 6,
                        bgcolor: "rgba(0,0,0,0.06)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 6,
                            bgcolor: getMuiProgressColor(parseFloat(localProg)),
                            transition: "transform 0.4s ease",
                        },
                    }}
                />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Toolbar: action buttons spread out */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                    px: 0,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                        icon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Expand All" />}
                        size="small"
                        onClick={expandAll}
                        sx={{
                            fontSize: "0.7rem",
                            height: 30,
                            bgcolor: "rgba(0,0,0,0.04)",
                            "&:active": { transform: "scale(0.95)" },
                            transition: "transform 0.1s ease",
                        }}
                    />
                    <Chip
                        icon={<UnfoldLessIcon sx={{ fontSize: 16 }} />}
                        label={<Langs str="Collapse All" />}
                        size="small"
                        onClick={collapseAll}
                        sx={{
                            fontSize: "0.7rem",
                            height: 30,
                            bgcolor: "rgba(0,0,0,0.04)",
                            "&:active": { transform: "scale(0.95)" },
                            transition: "transform 0.1s ease",
                        }}
                    />
                </Box>
                <IconButton
                    onClick={exportProgress}
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "#FF9015",
                        color: "#fff",
                        borderRadius: 2,
                        "&:hover": { bgcolor: "#E8820F" },
                        "&:active": { transform: "scale(0.93)" },
                        transition: "all 0.15s ease",
                    }}
                >
                    <FileDownloadIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {/* PrimeReact Tree with mobile-friendly styling */}
            <Box
                sx={{
                    "& .p-tree": {
                        border: "none",
                        padding: 0,
                        background: "transparent",
                        fontFamily: '"Inter", "Roboto", "Helvetica", sans-serif',
                    },
                    "& .p-tree-filter-container": {
                        mb: 1,
                        "& input": {
                            fontSize: "16px",
                            borderRadius: "12px",
                            padding: "10px 14px",
                            minHeight: "48px",
                        },
                    },
                    "& .p-treenode-content": {
                        padding: "4px 8px",
                        minHeight: "48px",
                        borderRadius: "8px",
                        "&:hover": {
                            background: "rgba(0,0,0,0.03)",
                        },
                    },
                    "& .p-treenode-label": {
                        width: "100%",
                    },
                    "& .p-tree-toggler": {
                        width: "36px",
                        height: "36px",
                        marginRight: "4px",
                    },
                    "& .p-treenode-children": {
                        paddingLeft: "16px",
                    },
                }}
            >
                <Tree
                    value={treeData}
                    filter={true}
                    onNodeClick={(e) => viewCollabsRespNodeDoubleClick(e)}
                    nodeTemplate={nodeTemplate}
                    filterPlaceholder="Rechercher..."
                    className="w-full border-0"
                    expandedKeys={expandedKeys}
                    onToggle={(e) => setExpandedKeys(e.value)}
                />
            </Box>

            {/* Aptitude Modal (mobile) */}
            {selectedAptitude && (
                <MobileAptitudeModal
                    sessionNumber={sessionNumber}
                    maxSession={maxSession}
                    handleSessionNumberChange={(e) => handleSessionNumberChange(e)}
                    show={showModal}
                    handleClose={handleClose}
                    aptitude={findNodeByKey(nodes, selectedAptitude)}
                    edit={editAptitudeDetails || true}
                />
            )}
        </Box>
    );
}
